"use client";

import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { getMyAdminStatus } from "@/lib/admin.functions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { ADMIN_SESSION_KEY, clearAdminCache } from "@/lib/cockpit-session-cache";
import { clearMfaVerifiedCache } from "@/lib/mfa-session-cache";
import { useAuthChange } from "@/hooks/use-auth-event";


export type AdminSession = {
  status: "loading" | "anonymous" | "authenticated";
  isAdmin: boolean;
  isEditor: boolean;
  role: "admin" | "editor" | null;
  email: string | null;
  userId: string | null;
};

const anonymous: AdminSession = {
  status: "anonymous",
  isAdmin: false,
  isEditor: false,
  role: null,
  email: null,
  userId: null,
};

const loadingState: AdminSession = { ...anonymous, status: "loading" };

/**
 * Ticket KN6: wie `useCockpitSession` auf den gemeinsamen Zwischenspeicher
 * umgestellt. Layout und Seite teilen sich damit eine Abfrage statt zwei.
 */
export function useAdminSession(): AdminSession & { signOut: () => Promise<void> } {
  const getStatus = useServerFn(getMyAdminStatus);
  const queryClient = useQueryClient();

  // Nur bei echtem Kontowechsel oder Abmeldung leeren. Ein `SIGNED_IN` für
  // dasselbe Konto (Rückkehr in einen lange offenen Reiter) darf weder den
  // Ladezustand noch die 2FA-Bestätigung zurücksetzen – sonst blitzt die
  // Oberfläche auf, Klicks gehen verloren und die 2FA-Abfrage kommt erneut.
  const knownUserId = useRef<string | null>(null);
  useAuthChange(({ event, userId }) => {
    const previous = knownUserId.current;
    knownUserId.current = userId;
    if (event === "SIGNED_OUT" || (userId && previous && userId !== previous)) {
      clearAdminCache(queryClient);
      clearMfaVerifiedCache();
      return;
    }
    void queryClient.invalidateQueries({ queryKey: ADMIN_SESSION_KEY });
  });


  const { data, isError } = useQuery({
    queryKey: ADMIN_SESSION_KEY,
    queryFn: async (): Promise<AdminSession> => {
      // Ohne aktive Session hängt die Client-Middleware keinen Bearer-Token an
      // – die Server-Function würde mit "Unauthorized" fehlschlagen.
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session?.access_token) return anonymous;
      const result = await getStatus();
      return {
        status: "authenticated",
        isAdmin: result.isAdmin,
        isEditor: result.isEditor,
        role: result.role,
        email: result.email,
        userId: result.userId,
      };
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const state: AdminSession = data ?? (isError ? anonymous : loadingState);

  const signOut = async () => {
    await supabase.auth.signOut();
    clearAdminCache(queryClient);
    clearMfaVerifiedCache();
  };


  return { ...state, signOut };
}
